import { QueryReturnValue } from '@reduxjs/toolkit/query';
import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';
import { DocumentNode } from 'graphql';

import { GqlError } from '@/typings/graphql';
import { RootState } from '../store';
import { env } from '@/lib/env';
import { isMfeMode, getMfeMountProps } from '@/mfe/mfe-mode';
import { refreshAuth0Token } from '@/lib/auth0-token';
import { setSession } from '@/redux/features/session/slice';

export const isGqlError = (error: object): error is GqlError =>
  '__type' in error && error.__type === 'GqlError';

// ponytail: matches the backend's "JWS Protected Header is invalid" 500,
// thrown when the JWT can't be verified (expired/tampered/rotated key).
const isInvalidJwtError = (message?: string) =>
  Boolean(message && /jws protected header/i.test(message));

const getAuthHeader = async (state: RootState): Promise<string | undefined> => {
  const { session, invitationToken } = state.sessionSlice;

  if (invitationToken) {
    return `Invitation ${invitationToken}`;
  }

  if (isMfeMode()) {
    const accessToken = await getMfeMountProps().authToken([
      env.AUTH0_AUDIENCE,
    ]);
    return `Bearer ${accessToken}`;
  }

  if (session?.accessToken) {
    return `Bearer ${session.accessToken}`;
  }

  return undefined;
};

type GraphQlRequest = {
  document: string | DocumentNode;
  variables?: object | void;
};

type ApiParam = Parameters<BaseQueryFn<GraphQlRequest, object, GqlError>>[1];
type ExtraOptionsParam = Parameters<
  BaseQueryFn<GraphQlRequest, object, GqlError>
>[2];

const runGraphqlRequest = (
  args: GraphQlRequest,
  api: ApiParam,
  extraOptions: ExtraOptionsParam
) =>
  graphqlRequestBaseQuery({
    url: env.BACK_DASHBOARD_API_URL,
    prepareHeaders: async () => {
      const headers = new Headers();
      const authHeader = await getAuthHeader(api.getState() as RootState);

      if (authHeader) {
        headers.set('authorization', authHeader);
      }

      return headers;
    },
    customErrors(res) {
      const error = res.response.errors?.[0];
      const extensions = error?.extensions as
        | {
            statusCode?: number;
            name?: string;
            code?: string;
            stacktrace?: string[];
          }
        | undefined;
      const statusCode =
        extensions?.statusCode ||
        (error as { status?: number } | undefined)?.status ||
        res.response.status;
      const name = extensions?.name || extensions?.code;
      const message =
        error?.message ||
        // Plain HTTP errors (e.g. auth middleware rejecting the JWT before
        // GraphQL runs) come back as the raw response body, not `errors[]`.
        (res.response as { message?: string }).message ||
        (statusCode
          ? `Request failed with status code ${statusCode}`
          : 'Unknown error');

      return {
        __type: 'GqlError',
        stack: extensions?.stacktrace?.map((entry) => String(entry)) ?? [],
        name,
        message,
        code: statusCode ? String(statusCode) : '500',
        notifySentry: Number(statusCode) >= 500,
      };
    },
  })(args, api, extraOptions);

const dynamicBaseQuery: BaseQueryFn<GraphQlRequest, object, GqlError> = async (
  args,
  api,
  extraOptions
) => {
  let result = await runGraphqlRequest(args, api, extraOptions);

  if (isInvalidJwtError(result.error?.message)) {
    const freshToken = await refreshAuth0Token();
    const state = api.getState() as RootState;

    if (freshToken && state.sessionSlice.session) {
      api.dispatch(
        setSession({ ...state.sessionSlice.session, accessToken: freshToken })
      );
      result = await runGraphqlRequest(args, api, extraOptions);
    }
  }

  return result as QueryReturnValue<object, GqlError, object>;
};

export const backDashboardApi = createApi({
  reducerPath: 'backDashboardApi',
  baseQuery: dynamicBaseQuery,
  keepUnusedDataFor: 60,
  endpoints: () => ({}),
});
